/**
 * useGeocoding Hook
 * Manages geocoding and reverse geocoding operations
 */

import { useState, useCallback } from 'react';
import { geocodeAddress, reverseGeocode as reverseGeocodeCore } from '../core/googleMapsCore';

/**
 * Custom hook for geocoding operations
 * @returns {Object} Geocoding state and methods
 */
export function useGeocoding() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Convert address to coordinates
   * @param {string} address - Address to geocode
   * @param {Object} options - Geocoding options
   * @returns {Promise<Object|null>} Location data
   */
  const geocode = useCallback(async (address, options = {}) => {
    if (!address) {
      setError('Address is required');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await geocodeAddress(address, options);
      return result;
    } catch (err) {
      console.error('[useGeocoding] Geocode error:', err);
      setError(err.message || 'Failed to geocode address');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Convert coordinates to address
   * @param {Object} location - { lat, lng }
   * @returns {Promise<string|null>} Formatted address
   */
  const reverseGeocode = useCallback(async (location) => {
    if (!location?.lat || !location?.lng) {
      setError('Valid coordinates are required');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const address = await reverseGeocodeCore(location);
      return address;
    } catch (err) {
      console.error('[useGeocoding] Reverse geocode error:', err);
      setError(err.message || 'Failed to get address');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    geocode,
    reverseGeocode,
    clearError,
  };
}
